import {
  IOnboardingJourneyFlowType,
  IUserOnboardingFlowStateEnum,
} from '@r-client/data/graphql-types';
import { E_USER_INTENT } from '@r-client/republic/util/route-intent';

/**
 * `user.onboardingFlowState` is the server-side memory of where the user is in
 * the onboarding funnel, and it carries two things at once:
 *
 * - whether to resume onboarding at all — anything other than `Inactive`
 * - which flow they picked — `Undecided` means "on the welcome page, no choice yet"
 *
 * These helpers translate the two client-side notions of "which flow" (a
 * destination route's intent, and the `flow_type` URL param) into that enum.
 */

/**
 * Whether a login or registration heading to a destination with an intent (invest
 * or trade) is routed through onboarding first. Disabled for now: those users go
 * straight to their destination, and only plain logins/registrations onboard.
 */
// TODO: https://linear.app/republic/issue/USMKT-2447/fe-re-enable-intent-based-onboarding-redirection-for-phase-2-show-only
// re-enable, with updated logic, for Phase 2 onboarding — show only the steps the
// intended action requires. Also gates the trading redirection of USMKT-2195.
export const IS_INTENT_REDIRECTION_ENABLED = false;

/** The flow implied by a destination route, or null when the route implies none. */
export const intentToFlowState = (
  intent: E_USER_INTENT | null
): IUserOnboardingFlowStateEnum | null => {
  switch (intent) {
    case E_USER_INTENT.Invest:
      return IUserOnboardingFlowStateEnum.Investment;
    case E_USER_INTENT.Trade:
      return IUserOnboardingFlowStateEnum.Trading;
    default:
      return null;
  }
};

/**
 * Maps a raw URL query param to a matching enum value (case-insensitive).
 * Returns undefined for missing or unrecognized values.
 */
const parseEnumParam = <T extends Record<string, string>>(
  enumObj: T,
  raw: string | string[] | undefined
): T[keyof T] | undefined => {
  const value = (Array.isArray(raw) ? raw[0] : raw)?.toUpperCase();
  return Object.values(enumObj).find((v) => v === value) as
    | T[keyof T]
    | undefined;
};

/** The onboarding page's `flow_type` param, as the flow-type enum. */
export const parseFlowTypeParam = (
  raw: string | string[] | undefined
): IOnboardingJourneyFlowType | undefined =>
  parseEnumParam(IOnboardingJourneyFlowType, raw);

/** The flow the user is entering, from the onboarding page's `flow_type` param. */
export const flowTypeToFlowState = (
  flowType: IOnboardingJourneyFlowType | undefined
): IUserOnboardingFlowStateEnum | null => {
  switch (flowType) {
    case IOnboardingJourneyFlowType.Investment:
      return IUserOnboardingFlowStateEnum.Investment;
    case IOnboardingJourneyFlowType.Trading:
      return IUserOnboardingFlowStateEnum.Trading;
    default:
      return null;
  }
};

/**
 * Value of the `context` param the registration entries put on their onboarding
 * URL. Exported so the builders and the reader can't drift.
 */
export const REGISTRATION_CONTEXT = 'register';

/**
 * Whether the user arrived at onboarding straight from registration.
 *
 * This is what tells a brand-new user (whose journey still has to be created from
 * their flow state) apart from a legacy user who legitimately has no journey, and
 * from a login resume whose journey already exists. Client-side routing only: it
 * is deliberately not the deprecated GraphQL `onboardingContext` argument, and is
 * never sent to the API.
 */
export const isRegistrationContext = (
  raw: string | string[] | undefined
): boolean =>
  (Array.isArray(raw) ? raw[0] : raw)?.toLowerCase() === REGISTRATION_CONTEXT;

/**
 * Whether a stored state means "resume onboarding". A null state (backend hasn't
 * backfilled, or the user never entered the funnel) reads as inactive.
 */
export const isOnboardingActive = (
  state: IUserOnboardingFlowStateEnum | null | undefined
): boolean => state != null && state !== IUserOnboardingFlowStateEnum.Inactive;
